import { graphHooks } from '@tunasong/graph-lib/react'
import { useSelector } from '@tunasong/redux'
import cn from 'classnames'
import { type HTMLAttributes, type PropsWithChildren, type Ref } from 'react'
import { useNavigate } from 'react-router'
import { PreviewCard } from '../primitives/preview-card.js'
import { ProfileAvatarImage } from './profile-avatar-image.js'
import { ProfileHoverCard } from './profile-hover-card.js'
import moduleStyles from './user-avatar.module.css'

const avatarSizePx: Record<string, number> = {
  micro: 20,
  tiny: 24,
  small: 32,
  medium: 40,
  large: 56,
}

type AvatarSize = 'micro' | 'tiny' | 'small' | 'medium' | 'large'

/** Ids that are not people: no profile card */
const nonPersonIds = new Set(['assistant', 'PUBLIC', 'AUTHENTICATED'])

export interface UserAvatarProps extends HTMLAttributes<HTMLDivElement>, PropsWithChildren {
  className?: string
  userId?: string
  size?: AvatarSize
  showName?: boolean
  /** Active link. @default true */
  link?: boolean
  /** Use a picture of the user (if available) */
  picture?: boolean
  /** fallbackName when userId is not set - e.g., before the user is registered */
  fallbackName?: string
  /** Phase 2 dashboard: prefix the accessible label with "Last edited by " for SR users. */
  lastEditedContext?: boolean
  /** Open the user's profile card on hover or focus. Off where the avatar is itself a menu or the card. @default true */
  profileCard?: boolean
  ref?: Ref<HTMLDivElement>
}

export const UserAvatar = ({
  userId,
  className,
  size = 'medium',
  showName = false,
  ref,
  link = true,
  picture = true,
  fallbackName,
  onClick,
  style,
  lastEditedContext = false,
  profileCard = true,
  ...restProps
}: UserAvatarProps) => {
  const loggedInId = useSelector(state => state.user.userId)
  const { profile } = graphHooks.useProfile(userId)
  const navigate = useNavigate()

  const url = userId === loggedInId ? '/app/profile' : undefined

  const name = profile?.name ?? profile?.email ?? fallbackName
  const lastEditedLabel = lastEditedContext && name ? `Last edited by ${name}` : undefined
  const altText = lastEditedLabel ?? name
  const px = avatarSizePx[size]

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      event.stopPropagation()
      onClick(event)
      return
    }
    if (link && url) {
      event.stopPropagation()
      void navigate(url)
    }
  }

  // A11Y: the avatar is interactive when an onClick is supplied or when it
  // navigates to a profile via the link affordance. In both cases, make the
  // div keyboard-reachable with proper role + tabIndex + key handlers so
  // keyboard users can activate it.
  const isInteractive = Boolean(onClick) || (link && Boolean(url))
  const handleKeyDown = isInteractive
    ? (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleClick(event as unknown as React.MouseEvent<HTMLDivElement>)
        }
      }
    : undefined

  if (!profile) {
    return null
  }

  const avatar = (
    <div
      {...restProps}
      ref={ref}
      className={moduleStyles.root}
      style={style}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={isInteractive ? 'button' : lastEditedLabel ? 'img' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={lastEditedLabel ?? (isInteractive ? `${name ?? 'User'} profile` : undefined)}
    >
      <ProfileAvatarImage
        profile={profile}
        px={px}
        className={cn(className, link && url && moduleStyles.clickable)}
        picture={picture}
        alt={altText}
        fallbackName={fallbackName}
      />
      {showName ? <span className={moduleStyles.name}>{name}</span> : null}
    </div>
  )

  if (!profileCard || !userId || nonPersonIds.has(userId)) {
    return avatar
  }

  return (
    <PreviewCard trigger={avatar} aria-label={`${name ?? 'User'} profile`}>
      <ProfileHoverCard userId={userId} />
    </PreviewCard>
  )
}

export default UserAvatar
